import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useFormattedAmount } from "../../../../../../hooks/useFormattedAmount";
import { useNotification } from "../../../../../../hooks/useNotification";
import {
  useMutateMOAllocatedQtyMutation,
  useMutatePurchaseAllocatedQtyMutation,
  useMutateSalesOrderAllocatedQtyMutation,
  useMutateTransferAllocatedQtyMutation,
} from "../../../../../../store/apiSlice/orderApiSlice";
import { ReactComponent as EditIcon } from "../../../../assets/svgIcon/shipment/Edit.svg";

/**
 * EditAllocatedQty Component
 *
 * Allows inline editing of the allocated quantity for a given item.
 * Depending on the allocation type (purchase or transfer),
 * sends an update to the appropriate mutation and handles input validation.
 *
 * @component
 * @param {number|string} allocated_qty - Initial quantity value to be edited.
 * @param {string|number} id - Unique ID of the order line.
 * @param {string} allocationFor - Type of allocation (e.g., 'transfer', 'purchase').
 */

const EditAllocatedQty = ({ allocated_qty, id, allocationFor }) => {
  const notify = useNotification();
  const quantity = Number(allocated_qty).toFixed(2);
  const { precision } = useSelector(state => state.misc.settings);
  const formattedAmount = useFormattedAmount();
  const [isEditMode, setIsEditMode] = useState(false);
  const [value, setValue] = useState(quantity);
  const isSubmitting = useRef(false);

  const setFinalValue = async e => {
    if (isSubmitting.current) return;

    if (Number(value) !== Number(allocated_qty)) {
      isSubmitting.current = true;
      try {
        if (allocationFor === "transfer") {
          await mutateTransferAllocatedQty({ id, allocated_qty: value }).unwrap();
        } else if (allocationFor === "purchase") {
          await mutatePurchaseAllocatedQty({ id, allocated_qty: value }).unwrap();
        } else if (allocationFor === "salesOrder") {
          await mutateSalesOrderAllocatedQty({ id, requested_qty: value }).unwrap();
        } else if (allocationFor === "manufacturingOrder") {
          await mutateMOAllocatedQty({ id, requested_qty: value }).unwrap();
        }
        notify({ type: "success", message: "Successfully updated allocated quantity" });
      } catch (error) {
        notify({
          type: "error",
          message:
            (Array.isArray(error.data?.detail)
              ? error.data.detail.join(" ")
              : error.data?.detail) ||
            error.data?.allocated_qty?.[0] ||
            error.data?.requested_qty?.[0] ||
            "Something went wrong",
        });
        setValue(quantity);
      } finally {
        isSubmitting.current = false;
      }
    }
    setIsEditMode(false);
  };

  const handleEnterEvent = async e => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      await setFinalValue();
    }
  };

  useEffect(() => {
    setValue(Number(quantity).toFixed(precision));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocated_qty]);

  const [mutateTransferAllocatedQty, { isLoading }] = useMutateTransferAllocatedQtyMutation();
  const [mutatePurchaseAllocatedQty, { isLoading: isLoadingPurchase }] =
    useMutatePurchaseAllocatedQtyMutation();
  const [mutateSalesOrderAllocatedQty, { isLoading: isLoadingSalesOrder }] =
    useMutateSalesOrderAllocatedQtyMutation();
  const [mutateMOAllocatedQty, { isLoading: isLoadingMO }] = useMutateMOAllocatedQtyMutation();

  const isUpdating = isLoading || isLoadingPurchase || isLoadingSalesOrder || isLoadingMO;

  return (
    <AllocatedDataCss className="allocatedQty rightAlign">
      <div className="allocatedCellInner">
        <div className="qtyArea">
          {isUpdating ? (
            <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
          ) : isEditMode ? (
            <input
              autoFocus={true}
              type="number"
              value={value}
              onBlur={e => setFinalValue(e)}
              onChange={event => setValue(event.target.value)}
              onKeyDown={handleEnterEvent}
            />
          ) : (
            formattedAmount(value)
          )}
        </div>
        <div className="iconSlot">
          {!isUpdating && !isEditMode && (
            <span onClick={() => setIsEditMode(true)} className="iconWrapper">
              <EditIcon />
            </span>
          )}
        </div>
      </div>
    </AllocatedDataCss>
  );
};

export { EditAllocatedQty };

/*
 * Permanent right slot for the edit icon (icon + spacing).
 * ListView allocatedHeader / allocatedTotal must use the same slot width
 * as extra padding beyond the shared 16px cell padding (16 + 24 = 40).
 */
const ALLOCATED_ICON_SLOT = "24px";

const AllocatedDataCss = styled.td`
  text-align: right;
  min-width: 103px;

  .allocatedCellInner {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
  }

  .qtyArea {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    text-align: right;
  }

  .iconSlot {
    flex: 0 0 ${ALLOCATED_ICON_SLOT};
    width: ${ALLOCATED_ICON_SLOT};
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .iconWrapper {
    cursor: pointer;
    display: inline-flex;
    line-height: 0;
  }

  input {
    width: 100%;
    box-sizing: border-box;
    text-align: right;
    border: none;
    text-decoration: underline;
    outline: none;
  }

  .spinner-border {
    color: var(--GREY, #a1a5b7);
  }
`;
